import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../Utils/Function';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import BackgroundEffect from './BackgroundEffect';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [mobileNo, setMobileNo] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const clearMessages = () => {
        setError('');
        setSuccess('');
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        clearMessages();
        setIsLoading(true);

        try {
            const response = await axios.post(`${BASE_URL}/users/requestResetOtp.php`, {
                mobile_no: mobileNo
            });

            if (response.data.status === 200) {
                setSuccess(response.data.message || 'OTP sent successfully.');
                setStep(2);
            } else {
                setError(response.data.message || 'Unable to send OTP.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to connect to server. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        clearMessages();
        setIsLoading(true);

        try {
            const response = await axios.post(`${BASE_URL}/users/verifyResetOtp.php`, {
                mobile_no: mobileNo,
                otp_code: otpCode
            });

            if (response.data.status === 200) {
                setSuccess(response.data.message || 'OTP verified.');
                setStep(3);
            } else {
                setError(response.data.message || 'Invalid OTP.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'OTP verification failed.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        clearMessages();

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/users/resetPassword.php`, {
                mobile_no: mobileNo,
                otp_code: otpCode,
                new_password: newPassword
            });

            if (response.data.status === 200) {
                setSuccess(response.data.message || 'Password reset successful. Redirecting to login...');
                setTimeout(() => navigate('/login'), 1500);
            } else {
                setError(response.data.message || 'Failed to reset password.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Password reset failed.');
        } finally {
            setIsLoading(false);
        }
    };

    const renderStepForm = () => {
        if (step === 1) {
            return (
                <form onSubmit={handleRequestOtp}>
                    <div className="mb-4">
                        <label className="section-index mb-2" style={{ fontSize: '0.7rem' }}>REGISTERED MOBILE NUMBER</label>
                        <div className="auth-input-wrapper">
                            <input
                                type="tel"
                                className="auth-input w-100"
                                placeholder="Enter your registered mobile number"
                                value={mobileNo}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    if (val.length <= 10) setMobileNo(val);
                                }}
                                required
                            />
                        </div>
                    </div>
                    <button type="submit" className="btn-book-now w-100 py-3" disabled={isLoading}>
                        {isLoading ? 'SENDING OTP...' : 'SEND OTP'}
                    </button>
                </form>
            );
        }

        if (step === 2) {
            return (
                <form onSubmit={handleVerifyOtp}>
                    <div className="mb-4">
                        <label className="section-index mb-2" style={{ fontSize: '0.7rem' }}>ENTER OTP</label>
                        <div className="auth-input-wrapper">
                            <input
                                type="text"
                                className="auth-input w-100"
                                placeholder="Enter OTP sent to your mobile"
                                value={otpCode}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    if (val.length <= 6) setOtpCode(val);
                                }}
                                required
                            />
                        </div>
                    </div>
                    <button type="submit" className="btn-book-now w-100 py-3" disabled={isLoading}>
                        {isLoading ? 'VERIFYING OTP...' : 'VERIFY OTP'}
                    </button>
                    <button
                        type="button"
                        className="btn-book-now w-100 py-3 mt-3"
                        onClick={() => setStep(1)}
                        disabled={isLoading}
                    >
                        CHANGE MOBILE NUMBER
                    </button>
                </form>
            );
        }

        return (
            <form onSubmit={handleResetPassword}>
                <div className="mb-4">
                    <label className="section-index mb-2" style={{ fontSize: '0.7rem' }}>NEW PASSWORD</label>
                    <div className="auth-input-wrapper">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            className="auth-input"
                            placeholder="Enter new password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                        />
                        <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                    </div>
                </div>

                <div className="mb-5">
                    <label className="section-index mb-2" style={{ fontSize: '0.7rem' }}>CONFIRM PASSWORD</label>
                    <div className="auth-input-wrapper">
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            className="auth-input"
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                        />
                        <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                        >
                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                    </div>
                </div>

                <button type="submit" className="btn-book-now w-100 py-3" disabled={isLoading}>
                    {isLoading ? 'UPDATING PASSWORD...' : 'RESET PASSWORD'}
                </button>
            </form>
        );
    };

    return (
        <div className="auth-container d-flex align-items-center justify-content-center">
            <BackgroundEffect />
            <div className="auth-card p-4 p-md-5">
                <div className="text-center mb-4">
                    <h2 className="section-head-main mb-2" style={{ fontSize: '2rem' }}>FORGOT PASSWORD</h2>
                    <div style={{ width: '40px', height: '2px', backgroundColor: 'var(--secondary-color)', margin: '0 auto' }}></div>
                    <p className="mt-3 mb-0 text-white-50 small">
                        Step {step} of 3
                    </p>
                </div>

                {error && <div className="alert alert-danger bg-transparent text-danger border-danger small p-2 mb-4">{error}</div>}
                {success && <div className="alert alert-success bg-transparent text-success border-success small p-2 mb-4">{success}</div>}

                {renderStepForm()}

                <div className="text-center mt-4 pt-2">
                    <p className="m-0" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        Remembered your password?{' '}
                        <Link to="/login" style={{ color: 'var(--secondary-color)', textDecoration: 'none' }}>
                            Back to Login
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
